// The in-product spotlight tutorial: a NON-BLOCKING ring around the element the current step
// explains, and a card next to it with the step's title, Markdown body and Back / Next / Skip. It
// never dims the page or swallows clicks — tours tell people to click real things — and only the card
// takes pointer events; it can be dragged by its header out of the way. Explanatory only: it never
// changes data. Mounted once in the shell (TourHost) so it survives the route changes its steps make.
//
// A step names what to spotlight by its `data-tour` anchor (src/tour/launch.ts); a step whose anchor
// is not on the page within WAIT_MS shows as a centred card that says so. Keys: ← → (and Enter) step
// from the page background or the card, never while typing; Esc leaves.
import { ChevronLeft, ChevronRight, GraduationCap, X } from 'lucide-react';
import * as React from 'react';
import { Markdown } from '@cortex/ui';
import type { TourStep } from './launch.ts';

/** What the overlay runs: a launched guide tour (launch.ts `Tour`) or a built-in one. */
export interface SpotlightTour {
  id: string;
  title: string;
  steps: TourStep[];
  start: number;
}

const PAD = 6; // spotlight padding around the target
const TIP_W = 380; // card width next to a target
const CENTER_W = 460; // card width when centred
const MARGIN = 12; // minimum distance from the viewport edges
const WAIT_MS = 4000; // how long a step looks for its anchor

const SEEN = (id: string) => `cortex-tutorial-seen:${id}`;
export function markTourSeen(id: string) {
  try {
    localStorage.setItem(SEEN(id), '1');
  } catch {
    /* not remembered */
  }
}
export function isTourSeen(id: string) {
  try {
    return localStorage.getItem(SEEN(id)) === '1';
  } catch {
    return false;
  }
}

type Rect = { top: number; left: number; width: number; height: number };

const findAnchor = (anchor: string) =>
  document.querySelector<HTMLElement>(`[data-tour="${CSS.escape(anchor)}"]`);

function measure(el: HTMLElement): Rect | null {
  const r = el.getBoundingClientRect();
  if (!r.width && !r.height) return null;
  return {
    top: Math.round(r.top - PAD),
    left: Math.round(r.left - PAD),
    width: Math.round(r.width + PAD * 2),
    height: Math.round(r.height + PAD * 2),
  };
}

/** The card's position near the spotlight, clamped to the viewport; `h` is the card's real height. */
export function cardPosition(rect: Rect, h: number, vw: number, vh: number) {
  const clampX = (x: number) => Math.max(MARGIN, Math.min(x, vw - TIP_W - MARGIN));
  const clampY = (y: number) => Math.max(MARGIN, Math.min(y, vh - h - MARGIN));
  if (rect.left + rect.width + TIP_W + MARGIN < vw)
    return { top: clampY(rect.top), left: rect.left + rect.width + 10 };
  if (rect.top + rect.height + h + MARGIN < vh)
    return { top: rect.top + rect.height + 10, left: clampX(rect.left) };
  if (rect.top - h - MARGIN > 0) return { top: rect.top - h - 10, left: clampX(rect.left) };
  if (rect.left - TIP_W - MARGIN > 0) return { top: clampY(rect.top), left: rect.left - TIP_W - 10 };
  return { top: clampY(rect.top + rect.height + 10), left: clampX(rect.left) };
}

const same = (a: Rect | null, b: Rect | null) =>
  !!a &&
  !!b &&
  Math.abs(a.top - b.top) < 2 &&
  Math.abs(a.left - b.left) < 2 &&
  Math.abs(a.width - b.width) < 2 &&
  Math.abs(a.height - b.height) < 2;

// Guide images need the guide's asset route; in a tour card the alt text stands in.
const asset = (_name: string, alt: string) => <em className="text-page-muted">{alt || 'image'}</em>;

export function Spotlight({
  tour,
  onClose,
  navigate,
}: {
  tour: SpotlightTour;
  onClose: (completed: boolean) => void;
  /** The host's client-side navigation (a step's path). */
  navigate: (to: string) => void;
}) {
  const n = tour.steps.length;
  const [idx, setIdx] = React.useState(() => Math.max(0, Math.min(tour.start, n - 1)));
  const [rect, setRect] = React.useState<Rect | null>(null);
  const [settled, setSettled] = React.useState(false);
  const [cardH, setCardH] = React.useState(220);
  // Once dragged, the card stays where it was parked for the rest of the tour.
  const [pinned, setPinned] = React.useState<{ top: number; left: number } | null>(null);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const drag = React.useRef<{ dx: number; dy: number } | null>(null);
  const step = tour.steps[idx];

  const finish = React.useCallback(
    (completed: boolean) => {
      markTourSeen(tour.id);
      onClose(completed);
    },
    [tour.id, onClose],
  );
  const go = React.useCallback(
    (k: number) => {
      if (k < 0) return;
      if (k >= n) finish(true);
      else setIdx(k);
    },
    [n, finish],
  );

  // Per step: open its page, then look for its anchor.
  React.useEffect(() => {
    if (!step) return;
    let cancelled = false;
    setSettled(false);
    setRect(null);
    if (step.path) {
      const q = step.path.indexOf('?');
      const want = q === -1 ? step.path : step.path.slice(0, q);
      if (window.location.pathname !== want) navigate(step.path);
    }
    if (!step.anchor) {
      setSettled(true);
      return;
    }
    const anchor = step.anchor;
    const t0 = Date.now();
    let timer = 0;
    const look = () => {
      if (cancelled) return;
      const el = findAnchor(anchor);
      const m = el && measure(el);
      if (el && m) {
        el.scrollIntoView({ block: 'nearest' });
        setRect(measure(el) ?? m);
        setSettled(true);
      } else if (Date.now() - t0 < WAIT_MS) timer = window.setTimeout(look, 150);
      else setSettled(true);
    };
    look();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [step, navigate]);

  // Keep the ring on its target through scrolls and layout shifts.
  React.useEffect(() => {
    if (!step?.anchor || !settled) return;
    const anchor = step.anchor;
    const t = window.setInterval(() => {
      const el = findAnchor(anchor);
      const m = el ? measure(el) : null;
      if (m) setRect((prev) => (same(prev, m) ? prev : m));
    }, 250);
    return () => window.clearInterval(t);
  }, [step, settled]);

  // ← → Enter step (not while typing, not in an open popover); Esc leaves.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing =
        !!t && (['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) || t.isContentEditable === true);
      const popover = !!document.querySelector('[data-radix-popper-content-wrapper]');
      const inCard = !!t?.closest?.('[data-tutorial-card]');
      const background = !t || t === document.body || t === document.documentElement;
      const steppable = !typing && !popover && (inCard || background);
      if (e.key === 'Escape') {
        if (popover) return;
        e.preventDefault();
        finish(false);
      } else if ((e.key === 'ArrowRight' || e.key === 'Enter') && steppable) {
        e.preventDefault();
        go(idx + 1);
      } else if (e.key === 'ArrowLeft' && steppable) {
        e.preventDefault();
        go(idx - 1);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [idx, go, finish]);

  // The card's real height, so its placement never pushes the controls off screen.
  React.useLayoutEffect(() => {
    const h = cardRef.current?.offsetHeight;
    if (h && Math.abs(h - cardH) > 4) setCardH(h);
  });

  const clamp = React.useCallback(
    (top: number, left: number) => {
      const w = cardRef.current?.offsetWidth ?? TIP_W;
      const h = cardRef.current?.offsetHeight ?? cardH;
      return {
        top: Math.max(
          MARGIN,
          Math.min(top, window.innerHeight - Math.min(h, window.innerHeight - 2 * MARGIN) - MARGIN),
        ),
        left: Math.max(MARGIN, Math.min(left, window.innerWidth - w - MARGIN)),
      };
    },
    [cardH],
  );
  const onDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('button')) return;
    const r = cardRef.current?.getBoundingClientRect();
    if (!r) return;
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    setPinned({ top: r.top, left: r.left });
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  };
  const onDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (drag.current) setPinned(clamp(e.clientY - drag.current.dy, e.clientX - drag.current.dx));
  };
  const onDragEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };
  React.useEffect(() => {
    if (!pinned) return;
    const onResize = () => setPinned((p) => (p ? clamp(p.top, p.left) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [pinned, clamp]);

  if (!step) return null;
  const tip = rect ? cardPosition(rect, cardH + 8, window.innerWidth, window.innerHeight) : null;
  const style: React.CSSProperties = pinned
    ? { top: pinned.top, left: pinned.left, width: tip ? TIP_W : CENTER_W }
    : tip
      ? { top: tip.top, left: tip.left, width: TIP_W, transition: 'top .3s ease, left .3s ease' }
      : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: CENTER_W };
  const missing = settled && !rect && !!step.anchor;

  return (
    <div
      className="fixed inset-0 pointer-events-none z-[100000]"
      data-testid="tour-overlay"
      data-tour-id={tour.id}
      data-tutorial-step={idx}
      data-tutorial-settled={settled ? '1' : '0'}
    >
      {rect && (
        <div
          data-testid="tour-spotlight"
          data-anchor={step.anchor ?? ''}
          className="absolute rounded-[10px] pointer-events-none"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            boxShadow:
              '0 0 0 2px rgba(59,130,246,0.95), 0 0 0 6px rgba(59,130,246,0.22), 0 0 18px 4px rgba(59,130,246,0.35)',
            transition: 'top .3s ease, left .3s ease, width .3s ease, height .3s ease',
          }}
        />
      )}
      <div
        ref={cardRef}
        role="dialog"
        aria-label={`Tutorial: ${tour.title}`}
        data-tutorial-card
        data-testid="tour-card"
        data-pinned={pinned ? '1' : undefined}
        className="absolute rounded-[10px] bg-card-bg text-page-text shadow-2xl border border-page-divider overflow-hidden select-none pointer-events-auto max-w-[calc(100vw-24px)]"
        style={style}
      >
        <div className="h-1 bg-page-bg">
          <div
            className="h-1 bg-nav-active transition-all duration-300"
            style={{ width: `${((idx + 1) / n) * 100}%` }}
          />
        </div>
        <div
          data-testid="tour-drag"
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          title="Drag to move"
          className="flex items-center justify-between px-4 pt-3 cursor-grab active:cursor-grabbing touch-none"
        >
          <div className="flex items-center gap-2 text-xs font-semibold text-nav-active min-w-0">
            <GraduationCap size={14} className="shrink-0" /> <span className="truncate">{tour.title}</span>
          </div>
          <button
            type="button"
            data-testid="tour-skip"
            onClick={() => finish(false)}
            title="Skip the tutorial"
            aria-label="Skip the tutorial"
            className="p-1 rounded text-page-muted hover:text-page-text hover:bg-page-bg border-0 bg-transparent cursor-pointer"
          >
            <X size={15} />
          </button>
        </div>
        <div className="px-4 pt-2 pb-1 max-h-[50vh] overflow-y-auto">
          <div className="text-[15px] font-semibold mb-1.5" data-testid="tour-title">
            {step.title}
          </div>
          <div className="text-[13px] leading-relaxed" data-testid="tour-body">
            <Markdown source={step.body} asset={asset} headingOffset={3} />
          </div>
          {!settled && step.anchor && (
            <div className="text-[11px] text-page-muted mt-2 italic">finding the element…</div>
          )}
          {missing && (
            <div className="text-[11px] text-page-muted mt-2 italic" data-testid="tour-missing">
              That part of the page isn’t visible right now — the explanation still applies.
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 px-3 py-2.5 mt-1 border-t border-page-divider">
          <button
            type="button"
            data-testid="tour-back"
            onClick={() => go(idx - 1)}
            disabled={idx === 0}
            className="flex items-center gap-1 h-8 px-3 rounded text-xs hover:bg-page-bg disabled:opacity-40 border-0 bg-transparent cursor-pointer text-page-text"
          >
            <ChevronLeft size={14} /> Back
          </button>
          <span className="text-[11px] text-page-muted ml-1" data-testid="tour-count">
            {idx + 1} / {n}
          </span>
          <button
            type="button"
            data-testid="tour-next"
            onClick={() => go(idx + 1)}
            className="flex items-center gap-1 h-8 px-4 rounded bg-nav-active text-white text-xs ml-auto border-0 cursor-pointer"
          >
            {idx >= n - 1 ? 'Finish' : 'Next'} <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
